import { useEffect } from 'react'
import { useMapStore } from '../../stores/mapStore.js'

/**
 * LatLonGrid — renders graticule lines (lat/lon grid) on the map.
 * Toggled via the 'grid' layer in the Overlays category.
 */
export default function LatLonGrid({ map }) {
  useEffect(() => {
    if (!map) return

    const SOURCE_ID = 'latlongrid-source'
    const LAYER_ID  = 'latlongrid-lines'
    const LABEL_ID  = 'latlongrid-labels'

    function buildGrid() {
      const features = []

      // Longitude lines every 10°
      for (let lon = -180; lon <= 180; lon += 10) {
        const coords = []
        for (let lat = -85; lat <= 85; lat += 1) coords.push([lon, lat])
        features.push({
          type: 'Feature',
          properties: { label: `${Math.abs(lon)}°${lon >= 0 ? 'E' : 'W'}`, type: 'lon' },
          geometry: { type: 'LineString', coordinates: coords },
        })
      }

      // Latitude lines every 10°
      for (let lat = -80; lat <= 80; lat += 10) {
        features.push({
          type: 'Feature',
          properties: { label: `${Math.abs(lat)}°${lat >= 0 ? 'N' : 'S'}`, type: 'lat' },
          geometry: { type: 'LineString', coordinates: [[-180, lat], [180, lat]] },
        })
      }

      // Special lines: equator, tropics, arctic/antarctic circles
      const specials = [
        { lat: 0, label: 'Equator' },
        { lat: 23.4364, label: 'Tropic of Cancer' },
        { lat: -23.4364, label: 'Tropic of Capricorn' },
        { lat: 66.5636, label: 'Arctic Circle' },
        { lat: -66.5636, label: 'Antarctic Circle' },
      ]
      for (const s of specials) {
        features.push({
          type: 'Feature',
          properties: { label: s.label, type: 'special' },
          geometry: { type: 'LineString', coordinates: [[-180, s.lat], [180, s.lat]] },
        })
      }

      // Label points at intersections every 30°
      for (let lat = -60; lat <= 60; lat += 30) {
        for (let lon = -150; lon <= 150; lon += 30) {
          features.push({
            type: 'Feature',
            properties: {
              label: `${Math.abs(lat)}°${lat >= 0 ? 'N' : 'S'} ${Math.abs(lon)}°${lon >= 0 ? 'E' : 'W'}`,
              type: 'label',
            },
            geometry: { type: 'Point', coordinates: [lon, lat] },
          })
        }
      }

      return { type: 'FeatureCollection', features }
    }

    function setup() {
      try { if (map.getSource(SOURCE_ID)) return } catch { return }

      map.addSource(SOURCE_ID, { type: 'geojson', data: buildGrid() })

      // Grid lines
      map.addLayer({
        id: LAYER_ID,
        type: 'line',
        source: SOURCE_ID,
        filter: ['!=', ['get', 'type'], 'label'],
        paint: {
          'line-color': [
            'match', ['get', 'type'],
            'special', 'rgba(255,255,255,0.25)',
            'rgba(255,255,255,0.10)'
          ],
          'line-width': [
            'match', ['get', 'type'],
            'special', 1.2,
            0.5
          ],
          'line-dasharray': [4, 4],
        },
        layout: { visibility: 'none' },
      })

      // Labels
      map.addLayer({
        id: LABEL_ID,
        type: 'symbol',
        source: SOURCE_ID,
        filter: ['==', ['get', 'type'], 'label'],
        layout: {
          'text-field': ['get', 'label'],
          'text-size': 9,
          'text-font': ['Open Sans Regular'],
          'text-anchor': 'center',
          visibility: 'none',
        },
        paint: {
          'text-color': 'rgba(255,255,255,0.35)',
          'text-halo-color': 'rgba(0,0,0,0.5)',
          'text-halo-width': 1,
        },
      })
    }

    setup()

    // React to layer toggle
    const unsub = useMapStore.subscribe(
      (s) => {
        const gridLayer = s.activeLayers.find(l => l.id === 'grid')
        return `${gridLayer?.visible ?? false}|${gridLayer?.opacity ?? 0.8}`
      },
      (key) => {
        const [vis, opacity] = key.split('|')
        const visible = vis === 'true'
        try {
          if (map.getLayer(LAYER_ID)) {
            map.setLayoutProperty(LAYER_ID, 'visibility', visible ? 'visible' : 'none')
            map.setPaintProperty(LAYER_ID, 'line-opacity', parseFloat(opacity))
          }
          if (map.getLayer(LABEL_ID)) {
            map.setLayoutProperty(LABEL_ID, 'visibility', visible ? 'visible' : 'none')
            map.setPaintProperty(LABEL_ID, 'text-opacity', parseFloat(opacity))
          }
        } catch {}
      }
    )

    return () => {
      unsub()
      try {
        if (map.getLayer(LABEL_ID)) map.removeLayer(LABEL_ID)
        if (map.getLayer(LAYER_ID)) map.removeLayer(LAYER_ID)
        if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID)
      } catch {}
    }
  }, [map])

  return null
}
